import * as React from 'react';
import { ProductStatusFilterValue, sortInactiveToBottom } from './productStatusFilterUtils';

export type ProductSortOrder = 'ascend' | 'descend';
export type ProductSortKey = 'name' | 'lastUpdatedBy' | 'lastModifiedDate';

const PRODUCT_SORT_DIRECTIONS: ProductSortOrder[] = ['ascend', 'descend'];

const toTime = (value: unknown): number => {
  if (!value) return 0;
  const d = value instanceof Date ? value : new Date(value as any);
  const t = d.getTime();
  return Number.isFinite(t) ? t : 0;
};

export const sortProductsBySelection = <T extends Partial<Record<ProductSortKey, unknown>>>(
  data: T[],
  sortKey: ProductSortKey | null,
  sortOrder: ProductSortOrder
): T[] => {
  if (!sortKey) {
    // Preserve original order (e.g., backend ordering) until user sorts.
    return data;
  }
  const list = [...data];
  list.sort((a, b) => {
    let cmp = 0;
    if (sortKey === 'lastModifiedDate') {
      cmp = toTime(a.lastModifiedDate) - toTime(b.lastModifiedDate);
    } else if (sortKey === 'name') {
      cmp = String(a.name ?? '').localeCompare(String(b.name ?? ''), undefined, { sensitivity: 'base' });
    } else {
      cmp = String(a.lastUpdatedBy ?? '').localeCompare(String(b.lastUpdatedBy ?? ''), undefined, { sensitivity: 'base' });
    }
    return sortOrder === 'ascend' ? cmp : -cmp;
  });
  return list;
};

export const sortProductsWithStatus = <T extends Partial<Record<ProductSortKey, unknown>> & { active?: boolean }>(
  data: T[],
  sortKey: ProductSortKey | null,
  sortOrder: ProductSortOrder,
  statusFilter: ProductStatusFilterValue = 'all'
): T[] => {
  let result = sortProductsBySelection(data, sortKey, sortOrder);
  if (statusFilter === 'all') {
    result = sortInactiveToBottom(result);
  }
  return result;
};

export const useProductGridSorting = (defaultKey: ProductSortKey | null = 'lastModifiedDate', defaultOrder: ProductSortOrder = 'descend') => {
  const [sortKey, setSortKey] = React.useState<ProductSortKey | null>(defaultKey);
  const [sortOrder, setSortOrder] = React.useState<ProductSortOrder>(defaultOrder);

  const getColumnSortProps = React.useCallback(
    (key: ProductSortKey) => ({
      sorter: true as const,
      sortDirections: PRODUCT_SORT_DIRECTIONS,
      sortOrder: sortKey === key ? sortOrder : undefined,
    }),
    [sortKey, sortOrder]
  );

  const handleSorterChange = React.useCallback((sorter: any, action?: string) => {
    if (action && action !== 'sort') return;
    const s = Array.isArray(sorter) ? sorter[0] : sorter;
    const nextKey = (s?.field ?? s?.columnKey ?? sortKey) as ProductSortKey | null | undefined;
    const nextOrder = s?.order as ProductSortOrder | undefined;
    if (!nextKey) return;
    if (!nextOrder) {
      const isSameColumn = nextKey === sortKey;
      const toggledOrder: ProductSortOrder =
        isSameColumn && sortOrder === 'ascend' ? 'descend' : 'ascend';
      setSortKey(nextKey as ProductSortKey);
      setSortOrder(toggledOrder);
      return;
    }
    if (nextKey === sortKey && nextOrder === sortOrder) return;
    setSortKey(nextKey as ProductSortKey);
    setSortOrder(nextOrder);
  }, [sortKey, sortOrder]);

  return {
    sortKey,
    sortOrder,
    getColumnSortProps,
    handleSorterChange,
  };
};